import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import {
  addtextFaceClass,
  addtextFaceFamily,
  addtextFacePreviewLabel,
  ensureAddtextFontFaces,
  mergeAddtextFonts,
  pickerFontValue,
} from '@/lib/submission-zones';

type Props = {
  value: string;
  onChange: (next: string) => void;
  fonts?: string[];
  id?: string;
  'aria-label'?: string;
  className?: string;
};

/**
 * Under-text font picker that shows each option in the real addtext face.
 * Native <select> cannot style option typefaces; this listbox loads
 * /api/fonts/file/:name via @font-face.
 */
export function AddtextFontSelect({
  value, onChange, fonts, id, 'aria-label': ariaLabel, className,
}: Props) {
  const names = mergeAddtextFonts(fonts);
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const current = pickerFontValue(value);

  useEffect(() => { ensureAddtextFontFaces(names); }, [names.join('|')]);

  useEffect(() => {
    if (!open) return;
    function onDoc(ev: MouseEvent) {
      if (!rootRef.current?.contains(ev.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={rootRef} className={cn('addtext-font-picker relative min-w-0 flex-1', className)}>
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel || 'Under-logo font'}
        className={cn(
          'addtext-font-trigger h-7 w-full rounded-[6px] border border-black/[0.08] bg-surface px-2 text-left text-[11px]',
          addtextFaceClass(current),
        )}
        style={{ fontFamily: addtextFaceFamily(current) }}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={addtextFaceClass(current)} style={{ fontFamily: addtextFaceFamily(current) }}>
          {addtextFacePreviewLabel(current)}
        </span>
        <span className="addtext-font-stored ml-1.5 font-sans text-[10px] opacity-55">{current}</span>
      </button>
      {open && (
        <div
          id={listId}
          role="listbox"
          className="addtext-font-menu absolute z-40 mt-0.5 max-h-60 w-full overflow-auto rounded-[6px] border border-black/[0.08] bg-surface shadow-lg"
        >
          {names.map((name) => (
            <button
              key={name}
              type="button"
              role="option"
              aria-selected={name === current}
              data-addtext-font-option={name}
              className={cn(
                'addtext-font-option flex w-full items-baseline gap-1.5 px-2.5 py-1.5 text-left text-[12px] hover:bg-black/[0.06]',
                addtextFaceClass(name),
              )}
              style={{ fontFamily: addtextFaceFamily(name) }}
              onClick={() => { onChange(name); setOpen(false); }}
            >
              <span className={addtextFaceClass(name)} style={{ fontFamily: addtextFaceFamily(name) }}>
                {addtextFacePreviewLabel(name)}
              </span>
              <span className="addtext-font-stored font-sans text-[10px] opacity-55">{name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
